import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import GridWrapper from '../../shared/components/layout/GridWrapper';

import Button from '../../shared/components/ui/Button';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import Table from '../../shared/components/layout/Table';

import user from '../../shared/assets/svg/organisation.svg';

import Modal from '../../shared/components/ui/Modal';
import EditCompany from '../components/forms/EditCompany';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getAllProjects, getAllUsers, getCompanyByID, updateCompany } from '../services/api/implementation/impl';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { transformData } from '../../super-admin/utils/transformData';
import { BsFiletypePdf } from 'react-icons/bs';
import { AiOutlineFileImage } from 'react-icons/ai';
import { FaRegFile } from 'react-icons/fa';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { Building2, Mail, MapPin, Phone } from 'lucide-react';

import { useSidebar } from '../../shared/contexts/SidebarContext';
import {
  buildReturnToFromLocation,
  navigateWithReturn,
  useSmartBack
} from '../../shared/navigation';
/*
  Helper Functions for Documents
*/
const getFileIcon = (fileName) => {
  const extension = fileName?.split('.')?.pop()?.toLowerCase();

  switch (extension) {
    case 'pdf':
      return <BsFiletypePdf size={40} />;
    case 'png':
    case 'jpg':
    case 'jpeg':
      return <AiOutlineFileImage size={40} />;
    default:
      return <FaRegFile size={40} />; // Default icon for other file types
  }
};

const truncateFileName = (fileName, maxLength = 15) => {
  if (!fileName) return 'Unnamed File';
  if (fileName.length <= maxLength) return fileName;
  const extension = fileName.split('.').pop();
  const nameWithoutExtension = fileName.slice(0, -(extension.length + 1));
  return `${nameWithoutExtension.slice(0, maxLength - 3)}...${extension}`;
};

const getDeduplicatedAddress = (address, city, state) => {
  if (address) {
    const segments = String(address)
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    const uniqueSegments = segments.filter(
      (segment, index) =>
        segments.findIndex((item) => item.toLowerCase() === segment.toLowerCase()) === index
    );

    if (uniqueSegments.length) return uniqueSegments.join(', ');
  }

  return [city, state].filter(Boolean).join(', ');
};

const normalizeDocuments = (documents) => {
  if (!documents) return [];

  if (Array.isArray(documents)) {
    return documents.flatMap((doc) => {
      if (typeof doc === 'string') {
        return [{ name: doc.split('/').pop(), url: doc }];
      }
      if (doc?.name && doc?.url) return [{ name: doc.name, url: doc.url }];
      if (doc?.url) return [{ name: doc.url.split('/').pop(), url: doc.url }];
      return [];
    });
  }

  if (typeof documents === 'object') {
    if (documents?.name && documents?.url) return [{ name: documents.name, url: documents.url }];
    if (documents?.url) return [{ name: documents.url.split('/').pop(), url: documents.url }];
  }

  return [];
};

const CompanyDetails = () => {
  const navigate = useNavigate();
  const { id: companyId } = useParams();
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/management/company' });
  const userId = Cookies.get('userId');

  /* 
    Local state
  */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openEditCompanyModal, setOpenEditCompanyModal] = useState(false);
  const [companyDataFetched, setCompanyDataFetched] = useState(false);
  const [upLiftNum, setUpliftNum] = useState(1);

  /* 
    Hook form
  */
  const { reset } = useForm();

  /* 
    Data fetching
  */
  const {
    isLoading,
    isError,
    data: company,
    refetch: refetchCompany
  } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => getCompanyByID(companyId)
  });

  const { data: users, refetch: refetchUsers } = useQuery({
    queryKey: ['users'],
    queryFn: () => getAllUsers()
  });

  const { data: allProjects } = useQuery({
    queryKey: ['projects', companyId],
    queryFn: () => getAllProjects()
  });

  console.log({ users });

  /* 
    Filtering Users for Partners
  */
  let filterByDesignationId = users?.data?.filter((user) => user.desgignation_id === 6);

  let filteredUsers = filterByDesignationId?.filter((user) =>
    user?.company_ids?.includes(parseInt(companyId))
  );

  let partnerNames = filteredUsers?.map((user) => ({
    id: user.id,
    name: user.name,
    email: user.email,
    phone_number: user.phone_number
  }));

  const partnerFormat = ['id', 'name', 'email', 'phone_number'];

  const PartnerFormatted = partnerNames?.map((item) => transformData(item, partnerFormat));

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const companyProjects = Array.isArray(allProjects)
    ? allProjects.filter((project) => Number(project?.company_id) === Number(companyId))
    : [];

  const activeProjects = companyProjects.filter((project) => {
    const projectStatus = String(project?.status || project?.project_status || '')
      .trim()
      .toLowerCase();

    if (!projectStatus) return true;
    return !['inactive', 'completed', 'closed', 'archived'].includes(projectStatus);
  });

  const projectTotals = companyProjects.reduce(
    (accumulator, project) => {
      const totalUnitsValue =
        project?.total_units ??
        project?.totalUnits ??
        project?.units_count ??
        project?.unit_count ??
        project?.no_of_units;
      const soldUnitsValue =
        project?.units_sold ??
        project?.sold_units ??
        project?.unitsSold ??
        project?.soldUnits ??
        project?.booked_units;

      return {
        totalUnits: accumulator.totalUnits + toNumber(totalUnitsValue),
        unitsSold: accumulator.unitsSold + toNumber(soldUnitsValue)
      };
    },
    { totalUnits: 0, unitsSold: 0 }
  );

  const companyMetrics = [
    { key: 'total-projects', label: 'Total Projects', value: companyProjects.length },
    { key: 'active-projects', label: 'Active Projects', value: activeProjects.length },
    { key: 'total-units', label: 'Total Units', value: projectTotals.totalUnits },
    { key: 'units-sold', label: 'Units Sold', value: projectTotals.unitsSold }
  ];
  const formattedCompanyAddress = getDeduplicatedAddress(
    company?.address,
    company?.city,
    company?.state
  );

  /* 
    Mutation for Updating Company
  */
  const { mutate: mutateCompany, isLoading: isLoadingNewUserMutation } = useMutation({
    mutationFn: (newCompany) => updateCompany(newCompany, companyId),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      mixpanel.track('Company Updated', {
        company_id: companyId,
        company_name: company?.company_name
      });
      toast.success('Successfully updated company');
      setIsSubmitting(false);
      console.log('Company updated successfully');
      reset();
      setOpenEditCompanyModal(false);
      await refetchCompany(); // UI data update
    },
    onError: (err) => {
      setIsSubmitting(false);
      mixpanel.track('Company Update Failed', {
        company_id: companyId,
        company_name: company?.company_name,
        error: err?.message
      });
      toast.error(err?.response?.data?.message || err?.message || 'Error updating company');
      console.error('Error updating company:', err);
    }
  });

  /* 
    Event handlers
  */
  const handleToggleAddCompanyModal = () => {
    setOpenEditCompanyModal((prev) => {
      const willOpen = !prev;
      if (willOpen) {
        mixpanel.track('Edit Company Modal Opened', {
          company_id: company?.id,
          company_name: company?.company_name
        });
      } else {
        mixpanel.track('Edit Company Modal Closed', {
          company_id: company?.id,
          company_name: company?.company_name
        });
      }
      return willOpen;
    });
  };

  const onSubmit = (data) => {
    console.log({ data });
    // Validations here
    mutateCompany(data);
  };

  const handleViewProjects = () => {
    mixpanel.track('View Projects Clicked', {
      company_id: company?.id,
      company_name: company?.company_name
    });
    navigateWithReturn(navigate, `/builder/management/company/${company?.id}/projects`, {
      returnTo: `/builder/management/company/${company?.id}`
    });
  };

  const [selectedFilter, setSelectedFilter] = useState('Partners');

  const handleButtonClick = (filter) => {
    setSelectedFilter(filter);
    if (filter === 'Partners') {
      mixpanel.track('Partners Tab Viewed', {
        company_id: company?.id,
        company_name: company?.company_name
      });
    } else if (filter === 'Documents') {
      mixpanel.track('Documents Tab Viewed', {
        company_id: company?.id,
        company_name: company?.company_name
      });
    }
  };

  /* 
    Helper method calls
  */
  const landsFormat = ['id', 'land_code', 'khasra_number', 'total_area', 'company_name'];
  const landsFormatted = []; // Lands table removed as per user request

  /* 
    Header data
  */
  const headerData = [
    {
      _id: 1,
      name: 'CIN',
      value: company?.cin ? company?.cin : '________'
    },
    {
      _id: 2,
      name: 'TIN',
      value: company?.tin ? company?.tin : '________'
    },
    {
      _id: 3,
      name: 'TAN',
      value: company?.tan ? company?.tan : '________'
    },
    {
      _id: 4,
      name: 'PAN',
      value: company?.pan ? company?.pan : '________'
    },
    {
      _id: 5,
      name: 'GST',
      value: company?.gst ? company?.gst : '________'
    },
    {
      _id: 6,
      name: 'Address',
      value: formattedCompanyAddress || '________'
    }
  ];

  console.log('Uplift num: ', upLiftNum);

  /* 
    Effects
  */

  // Track page view
  useEffect(() => {
    mixpanel.track('Company Details Viewed', {
      user_id: userId,
      company_id: companyId,
      timestamp: new Date().toISOString()
    });
  }, [userId, companyId]);

  useEffect(() => {
    if (openEditCompanyModal) {
      console.log('Preventing scroll');
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [openEditCompanyModal]);

  useEffect(() => {
    refetchCompany();
  }, [refetchCompany]);

  useEffect(() => {
    if (company && !companyDataFetched) {
      // toast.success(`Successfully fetched company`);
      setCompanyDataFetched(true);
    }
  }, [company, companyDataFetched]);

  // If it's a partnership, filter out CIN and TIN
  const filteredHeaderData =
    company?.company_type?.toLowerCase() === 'partnership'
      ? headerData.filter((item) => item.name !== 'CIN' && item.name !== 'TIN')
      : headerData;

  /*
    Extracting Documents
  */
  const companyDocuments = [
    { title: 'Company Logos', documents: company?.company_logo },
    { title: 'Coloniser License Documents', documents: company?.coloniser_license_doc }
    // Add more document fields as needed
  ];
  const flattenedCompanyDocuments = companyDocuments.flatMap((section) =>
    normalizeDocuments(section.documents).map((file) => ({
      ...file,
      type: section.title
    }))
  );

  /*
    JSX
  */
  return (
    <>
      {/* Edit Company Modal */}
      {openEditCompanyModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={84}
          name="Edit Company Details"
          onClose={handleToggleAddCompanyModal}
          upShift={(upLiftNum + 4) * 10}>
          <EditCompany
            onSubmit={onSubmit}
            data={company}
            isSubmitting={isSubmitting}
            onUpliftChange={(numPartners) => {
              console.log('Coming num partners: ', numPartners);
              setUpliftNum(() => numPartners);
            }}
            onClose={handleToggleAddCompanyModal}
          />
        </Modal>
      )}

      <Layout>
        <div
          className={`fixed z-[10] top-0 transition-all duration-300 ease-in-out ${
            collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
          }`}>
          <Header
            data={{ name: `Company > ${company?.company_name || 'Loading...'}` }}
            backArrow={true}
            onBack={onBack}
          />
        </div>

        <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
          <GridWrapper width={100}>
            <div className="mt-[7rem] flex w-full flex-col gap-6">
              <div className="w-full rounded-2xl border border-[#E5EAF1] bg-white px-6 py-6 shadow-sm md:px-8">
                <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
                  <div className="h-24 w-24 overflow-hidden rounded-2xl bg-[#2D5A5F]">
                    {company?.company_logo?.[0]?.url ? (
                      <img
                        src={company.company_logo[0].url}
                        alt="company_logo"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-white">
                        <Building2 size={34} />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div>
                        <h1 className="text-3xl font-semibold leading-tight text-[#1F3F4C]">
                          {company?.company_name || 'Company Name'}
                        </h1>
                        <p className="mt-1 inline-flex items-center gap-2 text-sm text-[#6B7280]">
                          <MapPin size={14} className="text-[#94A3B8]" />
                          {formattedCompanyAddress || '--'}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 md:justify-end">
                        <Button
                          displayType="secondary"
                          onClick={handleToggleAddCompanyModal}
                          customStyle={{
                            borderRadius: '10px',
                            padding: '9px 14px'
                          }}>
                          Edit
                        </Button>
                        <Button
                          onClick={handleViewProjects}
                          customStyle={{
                            borderRadius: '10px',
                            padding: '9px 14px'
                          }}>
                          View Projects
                        </Button>
                      </div>
                    </div>

                    <div className="mt-5 grid grid-cols-3 gap-x-6 gap-y-4">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9AA4B2]">
                          PAN Number
                        </p>
                        <p className="mt-1 text-[20px] leading-none font-semibold text-[#374151]">
                          {company?.pan || '________'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9AA4B2]">
                          GST Identification
                        </p>
                        <p className="mt-1 text-[20px] leading-none font-semibold text-[#374151]">
                          {company?.gst || '________'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9AA4B2]">
                          CIN Number
                        </p>
                        <p className="mt-1 text-[20px] leading-none font-semibold text-[#374151]">
                          {company?.cin || '________'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9AA4B2]">
                          TAN Number
                        </p>
                        <p className="mt-1 text-[20px] leading-none font-semibold text-[#374151]">
                          {company?.tan || '________'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9AA4B2]">
                          Primary Email
                        </p>
                        <p className="mt-1 inline-flex items-center gap-1 text-base font-semibold text-[#374151]">
                          <Mail size={14} className="text-[#9AA4B2]" />
                          {company?.primary_email || '--'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#9AA4B2]">
                          Contact Phone
                        </p>
                        <p className="mt-1 inline-flex items-center gap-1 text-base font-semibold text-[#374151]">
                          <Phone size={14} className="text-[#9AA4B2]" />
                          {company?.contact_phone || '--'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 max-w-[980px]">
                {companyMetrics.map((metric) => (
                  <div
                    key={metric.key}
                    className="w-full min-h-[96px] rounded-xl border border-[#E5EAF1] bg-white px-5 py-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#98A1AE]">
                      {metric.label}
                    </p>
                    <p className="mt-2 text-4xl font-semibold leading-none text-[#1F4D52]">
                      {metric.value.toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-[#E5EAF1] bg-white p-6 shadow-sm">
                <div className="mb-5 border-b border-[#E6EBF1]">
                  <div className="flex items-center gap-7 overflow-x-auto">
                    <button
                      type="button"
                      className={`relative pb-3 text-[15px] font-semibold whitespace-nowrap transition-colors ${
                        selectedFilter === 'Partners'
                          ? 'text-[#1F4D52]'
                          : 'text-[#7A8797] hover:text-[#526173]'
                      }`}
                      onClick={() => handleButtonClick('Partners')}>
                      Partners
                      {selectedFilter === 'Partners' && (
                        <span className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[#1F4D52] rounded-full" />
                      )}
                    </button>
                    <button
                      type="button"
                      className={`relative pb-3 text-[15px] font-semibold whitespace-nowrap transition-colors ${
                        selectedFilter === 'Documents'
                          ? 'text-[#1F4D52]'
                          : 'text-[#7A8797] hover:text-[#526173]'
                      }`}
                      onClick={() => handleButtonClick('Documents')}>
                      Documents
                      {selectedFilter === 'Documents' && (
                        <span className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[#1F4D52] rounded-full" />
                      )}
                    </button>
                  </div>
                </div>

                {selectedFilter === 'Partners' ? (
                  PartnerFormatted?.length ? (
                    <div className="overflow-auto">
                      <Table
                        headers={['Name', 'Email', 'Phone']}
                        identifier={'id'}
                        data={PartnerFormatted}
                      />
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-[#D5DEE8] bg-[#F8FAFC] px-4 py-8 text-center">
                      <p className="text-sm font-medium text-[#6B7280]">No partners found for this company</p>
                    </div>
                  )
                ) : (
                  <div className="w-full rounded-xl bg-[#F8FAFC] p-5 border border-[#E7EDF5]">
                    {flattenedCompanyDocuments.length ? (
                      <div className="overflow-x-auto rounded-xl border border-[#E4EAF1] bg-white">
                        <table className="min-w-full text-sm">
                          <thead className="bg-[#F8FAFC] border-b border-[#E7EBF1] text-[#64748B]">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold">Document Type</th>
                              <th className="px-4 py-3 text-left font-semibold">File Name</th>
                              <th className="px-4 py-3 text-left font-semibold">Preview</th>
                              <th className="px-4 py-3 text-left font-semibold">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {flattenedCompanyDocuments.map((file, index) => (
                              <tr
                                key={`${file.type}-${file.name}-${index}`}
                                className="border-b border-[#EEF2F6] last:border-b-0">
                                <td className="px-4 py-3 text-[#334155]">{file.type}</td>
                                <td className="px-4 py-3 text-[#0F172A] font-medium" title={file.name}>
                                  {truncateFileName(file.name, 42)}
                                </td>
                                <td className="px-4 py-3 text-[#334155]">
                                  <span className="inline-flex items-center justify-center h-8 w-8 rounded-md bg-[#EEF2F7]">
                                    {getFileIcon(file.name)}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    download
                                    className="inline-flex items-center rounded-md border border-[#D8E1EC] bg-white px-3 py-1.5 text-xs font-semibold text-[#2A565E] hover:bg-[#F7FAFC]">
                                    Open
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-dashed border-[#CBD7E3] bg-white px-4 py-10 text-center">
                        <p className="text-sm font-medium text-[#6B7280]">
                          No documents uploaded for this company yet.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </GridWrapper>
        </div>
      </Layout>
    </>
  );
};

export default CompanyDetails;
